import { useMemo } from "react";

import { styled, keyframes } from "@compiled/react";
import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import { BaseGrid } from "./base-grid";

type SkeletonGridProps = {
    numProducts: number;
    isMobile?: boolean;
};

const shimmer = keyframes({
    "0%": {
        backgroundPosition: "-300px 0"
    },
    "100%": {
        backgroundPosition: "1000px 0"
    }
});

const LoadingSkeleton = styled.div<{
    width?: string;
    height?: string;
    borderRadius?: string;
    marginLeftPx?: number;
    marginBottomPx?: number;
    marginRightPx?: number;
    marginTopPx?: number;
}>({
    animationDuration: "1.2s",
    animationFillMode: "forwards",
    animationIterationCount: "infinite",
    animationName: shimmer,
    animationTimingFunction: "linear",
    borderRadius: ({ borderRadius }) => (borderRadius ? borderRadius : "3px"),
    height: ({ height }) => (height ? height : "12px"),
    width: ({ width }) => (width ? width : "100%"),
    backgroundRepeat: "no-repeat",
    backgroundColor: token("color.skeleton", "grey"),
    backgroundImage: `linear-gradient( to right, ${token(
        "color.skeleton",
        "grey"
    )} 10%, ${token("color.skeleton.subtle", "grey")} 20%, ${token(
        "color.skeleton",
        "grey"
    )} 30% )`,
    marginRight: ({ marginRightPx }) =>
        marginRightPx ? `${marginRightPx}px` : "0px",
    marginTop: ({ marginTopPx }) => (marginTopPx ? `${marginTopPx}px` : "0px"),
    marginBottom: ({ marginBottomPx }) =>
        marginBottomPx ? `${marginBottomPx}px` : "0px",
    marginLeft: ({ marginLeftPx }) =>
        marginLeftPx ? `${marginLeftPx}px` : "0px"
});

const SkeletonTitle = () => (
    <LoadingSkeleton
        width="328px"
        height="16px"
        borderRadius="3px"
        data-testid="skeleton-title"
    />
);

const SkeletonItem = () => (
    <div
        data-testid="skeleton-item"
        css={css({
            display: "flex",
            flexDirection: "row",
            gap: token("space.200"),
            paddingBlockStart: token("space.400"),
            paddingBlockEnd: token("space.250")
        })}
    >
        <LoadingSkeleton width="44px" height="44px" borderRadius="3px" />
        <div
            css={css({
                display: "flex",
                flexDirection: "column",
                gap: token("space.150"),
                paddingBlockStart: token("space.100")
            })}
        >
            <LoadingSkeleton width="80px" height="8px" borderRadius="3px" />
            <LoadingSkeleton width="160px" height="8px" borderRadius="3px" />
        </div>
    </div>
);

export const SkeletonGrid = ({ numProducts, isMobile }: SkeletonGridProps) => {
    const skeletonProducts = useMemo(() => {
        const products = [];
        for (let i = 0; i < numProducts; i++) {
            products.push(<SkeletonItem key={i} />);
        }
        return products;
    }, [numProducts]);

    if (!numProducts) return null;

    return (
        <BaseGrid
            products={skeletonProducts}
            title={<SkeletonTitle />}
            isMobile={isMobile}
        />
    );
};
